// friendlier-challenges (Trello d7cn4sN9) — "Use the solution" on the challenge surface.
//
// Imported by challenge-chrome.js (never wired from main.js on its own). It owns:
//   * the failed-Check counter — CLIENT-side, per working copy, in sessionStorage
//     `pc-challenge-fails:v1:<attemptId>` with an in-memory fallback. A graded fail AND a
//     run-failure Check both count; a pass never resets it; once the offer is shown it stays.
//     This is a PROCESS gate, not security: the route can be called directly, and every fetch is
//     flagged to the teacher (`solutionUsedAt`), which is the real control (decisions Round 3).
//   * the offer — a "Use the solution" button in the Check result card and beside Check once the
//     count reaches the challenge's `solutionAfter` (default 3), only when `hasModelAnswer`;
//   * the in-page confirm panel (never `window.confirm`), the two POSTs
//     (`…/challengeSolution` and `…/challengeSolution/restore` — never in `editor-api.js`), and the
//     status element beside Check holding the message and "Get my code back".
//
// NOTHING is rendered for a non-owner viewer (a teacher looking at a student's copy) or on a
// read-only editor: `wireSolution` returns an inert controller.
//
// Replacing the code goes through editor-core's PUBLIC `core.load(payload)` (its `isLoading()`
// keeps a programmatic load out of the authenticity capture) and the Draft is then saved straight
// away through challenge-chrome's own flush, because a programmatic load may not mark the buffer
// dirty. editor-core.js / editor-api.js are not edited and the runner contract is unchanged.
//
// SHOW mode (solution-show-mode, Trello oZoimUII): when the payload carries
// `challengeAnswerKey.solutionMode === 'show'` (with `hasModelAnswer`) — or a reply says
// `mode: 'show'` — the button reads "Show the solution" and opens a READ-ONLY dialog
// (`.modal-backdrop.challenge-solution-view`, one tab per file, text only). The student's code is
// NEVER replaced: no confirm, no `core.load`, no Draft flush, no files posted, no "Get my code
// back". The dialog toggles only its `hidden` attribute, because `editor-shortcuts.js`
// `isSaveInert` is a live `.modal-backdrop:not([hidden])` query (Ctrl-S inert while open).
//
// SOLUTION PIECES (solution-pieces-merge, Trello daAruJ5U): when `challengeAnswerKey.pieceSteps`
// is non-empty, neither offer is rendered (the step panel in challenge-pieces.js owns the
// solution) and the page is in show mode. The controller exposes `isOffered()`, `onOffered(cb)`,
// `checking()`, `locked()` and `openView(opener)` for that panel; INERT has no-op versions.

export var DEFAULT_SOLUTION_AFTER = 3;

var COUNTER_PREFIX = 'pc-challenge-fails:v1:';

export var SOLUTION_COPY = {
  button: 'Use the solution',
  confirmTitle: 'Use the model solution?',
  confirmBody: 'Your code will be replaced with the model solution. We\'ll save your own code first so you can get it back.',
  cancel: 'Cancel',
  loading: 'Loading the solution…',
  loadFailed: 'Couldn\'t load the solution. Try again.',
  loaded: 'Model solution loaded. Press Check to finish.',
  restore: 'Get my code back',
  restoring: 'Bringing your code back…',
  restoreFailed: 'Couldn\'t bring your code back. Try again.',
  restored: 'Your code is back.',
  saveFailed: 'Couldn\'t save. Your change is only on this screen.',
  // Show mode.
  showButton: 'Show the solution',
  showLoading: 'Loading the solution…',
  viewTitle: 'The solution',
  viewGuide: 'Read it, close this, then change your own code.',
  viewClose: 'Close',
  emptyFile: 'This file is empty.'
};

// Show-mode tab order: the student's editor file order for names in both, then the remaining
// model files in reply order. Pure.
export function orderSolutionFiles(modelFiles, editorFiles) {
  var model = Array.isArray(modelFiles) ? modelFiles : [];
  var names = model.map(function (f) { return f.name; });
  var out = [];
  var used = {};
  (Array.isArray(editorFiles) ? editorFiles : []).forEach(function (f) {
    var at = f && typeof f.name === 'string' ? names.indexOf(f.name) : -1;
    if (at >= 0 && !used[f.name]) { used[f.name] = true; out.push(model[at]); }
  });
  model.forEach(function (f) { if (!used[f.name]) { used[f.name] = true; out.push(f); } });
  return out;
}

// The default tab: the kept name if still present, else main.py, else the first file.
export function defaultSolutionTab(files, keep) {
  if (!Array.isArray(files) || !files.length) { return -1; }
  var i;
  if (keep) { for (i = 0; i < files.length; i++) { if (files[i].name === keep) { return i; } } }
  for (i = 0; i < files.length; i++) { if (files[i].name === 'main.py') { return i; } }
  return 0;
}

// In-memory fallback for a tab whose sessionStorage is missing or throws (private mode, quota).
var memoryCounts = {};

/** Test-only reset of the in-memory counter fallback. */
export function __resetSolutionCounters() { memoryCounts = {}; }

export function counterKey(attemptId) {
  return COUNTER_PREFIX + String(attemptId || '');
}

// The effective threshold: the server already resolves it, but a missing / bad value falls back
// to the default rather than switching the offer off or on.
export function effectiveThreshold(value) {
  var n = Number(value);
  return (Number.isInteger(n) && n >= 1 && n <= 20) ? n : DEFAULT_SOLUTION_AFTER;
}

function storedCount(storage, key) {
  if (!storage) { return 0; }
  try {
    var raw = storage.getItem(key);
    var n = raw == null ? 0 : parseInt(raw, 10);
    return n > 0 ? n : 0;
  } catch (e) { return 0; }
}

export function readFailCount(storage, attemptId) {
  var key = counterKey(attemptId);
  return Math.max(storedCount(storage, key), memoryCounts[key] || 0);
}

export function bumpFailCount(storage, attemptId) {
  var key = counterKey(attemptId);
  var n = readFailCount(storage, attemptId) + 1;
  memoryCounts[key] = n;
  if (storage) {
    try { storage.setItem(key, String(n)); } catch (e) { /* the memory copy stands */ }
  }
  return n;
}

// `source` for `core.load`: the main.py file's content, else the first file's.
export function sourceForFiles(files) {
  if (!Array.isArray(files) || !files.length) { return ''; }
  for (var i = 0; i < files.length; i++) {
    if (files[i] && files[i].name === 'main.py') { return String(files[i].content == null ? '' : files[i].content); }
  }
  return String(files[0].content == null ? '' : files[0].content);
}

// A clean `[{ name, content }]` copy, or null when there is nothing usable.
export function cleanFiles(files) {
  if (!Array.isArray(files)) { return null; }
  var out = files.filter(function (f) { return f && typeof f.name === 'string' && f.name; })
    .map(function (f) { return { name: f.name, content: f.content == null ? '' : String(f.content) }; });
  return out.length ? out : null;
}

// The payload for `core.load`: the page's own payload (mode, asset base URL, manifest and assets
// are KEPT — a model answer needing its own assets is an accepted limit) with the new files.
export function solutionLoadPayload(current, files) {
  var next = {};
  var base = current || {};
  Object.keys(base).forEach(function (k) { next[k] = base[k]; });
  next.files = files;
  next.source = sourceForFiles(files);
  return next;
}

export function solutionUrl(courseCtx, restore) {
  var c = courseCtx || {};
  return '/api/courses/' + encodeURIComponent(c.courseId || '') +
    '/lessons/' + encodeURIComponent(c.lessonId || '') +
    '/materials/' + encodeURIComponent(c.materialId || '') +
    '/challengeSolution' + (restore ? '/restore' : '');
}

// POST JSON; resolves the parsed body on 2xx. A failure rejects with `serverMessage` set to the
// server's own message for a 4xx (a 5xx's Boom message is not written for students).
function postJson(fetchFn, url, body) {
  if (typeof fetchFn !== 'function') { return Promise.reject(new Error('challenge-solution: no fetch')); }
  return Promise.resolve(fetchFn(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })).then(function (res) {
    var parsed = res && typeof res.json === 'function'
      ? Promise.resolve(res.json()).catch(function () { return {}; })
      : Promise.resolve({});
    return parsed.then(function (data) {
      if (!res || !res.ok) {
        var err = new Error('challenge-solution: request failed (' + (res && res.status) + ')');
        var status = res ? res.status : 0;
        err.serverMessage = (status >= 400 && status < 500 && data && typeof data.message === 'string')
          ? data.message : '';
        throw err;
      }
      return data || {};
    });
  });
}

// `{ files, hasBackup }`. A 200 without files (a Joi failure answers a silent 200 with the
// handler skipped) is a failure, never an empty editor.
export function fetchSolution(fetchFn, courseCtx, parent, files) {
  return postJson(fetchFn, solutionUrl(courseCtx, false), { parent: parent, files: files || null })
    .then(function (data) {
      var f = cleanFiles(data.files);
      if (!f) { throw new Error('challenge-solution: no files in the reply'); }
      var out = { files: f, hasBackup: data.hasBackup === true };
      // solution-show-mode: the server says the challenge only SHOWS the solution.
      if (data.mode === 'show') { out.mode = 'show'; }
      return out;
    });
}

export function fetchRestore(fetchFn, courseCtx, parent) {
  return postJson(fetchFn, solutionUrl(courseCtx, true), { parent: parent })
    .then(function (data) {
      var f = cleanFiles(data.files);
      if (!f) { throw new Error('challenge-solution: no files in the reply'); }
      return { files: f };
    });
}

function defaultStorage() {
  try {
    return (typeof window !== 'undefined' && window.sessionStorage) ? window.sessionStorage : null;
  } catch (e) { return null; }
}

function defaultFetch() {
  return (typeof globalThis !== 'undefined' && typeof globalThis.fetch === 'function')
    ? globalThis.fetch.bind(globalThis) : null;
}

var INERT = {
  active: false,
  noteCheck: function () {},
  setChecking: function () {},
  // solution-pieces-merge: the step panel's view of the offer (all no-ops here).
  isOffered: function () { return false; },
  onOffered: function () {},
  checking: function () { return false; },
  locked: function () { return false; },
  openView: function () {}
};

/**
 * Wire "Use the solution" for one challenge page. Returns `{ active, noteCheck(passed),
 * setChecking(on) }` — challenge-chrome calls `setChecking` from its own, and `noteCheck` once
 * per finished Check (graded result or run failure; an aborted Check is not reported).
 *
 * opts: { doc, core, payload, answerKey, attemptId, courseCtx, isOwner, scheduleLocked,
 *         flush, hideResults, checkBtn, resultCard, resultFoot, fetch?, storage? }
 */
export function wireSolution(opts) {
  opts = opts || {};
  var doc = opts.doc;
  var core = opts.core;
  if (!doc || !core || opts.isOwner !== true) { return INERT; }
  if (typeof core.isReadOnly === 'function' && core.isReadOnly()) { return INERT; }

  var answerKey = opts.answerKey || {};
  var hasModelAnswer = answerKey.hasModelAnswer === true;
  // solution-pieces-merge: honoured pieces (`pieceSteps`) put everything in the step panel —
  // neither the toolbar button nor the `.cr-solution` result-card offer is rendered, and the
  // page is in show mode (the server already sends `solutionMode: 'show'`; this is the
  // defensive mirror, so the student's code is never swapped).
  var piecesOn = Array.isArray(answerKey.pieceSteps) && answerKey.pieceSteps.length > 0;
  // solution-show-mode: decided at load; a reply carrying `mode: 'show'` can switch a replace
  // page into show mode for the rest of the page load (never the other way).
  var showAtLoad = hasModelAnswer && (answerKey.solutionMode === 'show' || piecesOn);
  var threshold = effectiveThreshold(answerKey.solutionAfter);
  var storage = Object.prototype.hasOwnProperty.call(opts, 'storage') ? opts.storage : defaultStorage();
  var fetchFn = opts.fetch || defaultFetch();
  var attemptId = opts.attemptId || '';
  var courseCtx = opts.courseCtx || {};
  var scheduleLocked = opts.scheduleLocked === true;
  var flush = typeof opts.flush === 'function' ? opts.flush : function () { return Promise.resolve(null); };
  var hideResults = typeof opts.hideResults === 'function' ? opts.hideResults : function () {};

  var state = {
    count: readFailCount(storage, attemptId),
    offered: false,
    checking: false,
    resultShown: false,
    busy: false,
    restoring: false,
    // "Get my code back" uses the restore ROUTE when the page loaded with an unrestored backup
    // or a fetch reported one; otherwise the in-memory pre-solution copy (preview writes nothing).
    routeRestore: answerKey.hasSolutionBackup === true,
    memoryFiles: null,
    message: '',
    messageError: false,
    opener: null,
    // Show mode: the cached model files (one fetch per page load), the last-viewed file name and
    // the button whose press is loading.
    showMode: showAtLoad,
    cache: null,
    viewTab: null,
    pressed: null,
    // solution-pieces-merge: the control that opened the view from the step panel, and the
    // step panel's offer subscribers.
    viewOpener: null,
    offeredListeners: []
  };
  if (hasModelAnswer && state.count >= threshold) { state.offered = true; }

  function el(tag, cls, text) {
    var node = doc.createElement(tag);
    if (cls) { node.className = cls; }
    if (text != null) { node.textContent = text; }
    return node;
  }
  function button(id, cls, text) {
    var b = el('button', cls, text);
    b.setAttribute('type', 'button');
    b.id = id;
    b.setAttribute('id', id);
    return b;
  }

  // --- The status element beside Check: message (polite live region) + "Get my code back".
  var statusBox = el('span', 'challenge-solution-status');
  statusBox.id = 'challenge-solution-status';
  statusBox.setAttribute('id', 'challenge-solution-status');
  var statusMsg = el('span', 'challenge-solution-msg runner-status-line');
  statusMsg.setAttribute('role', 'status');
  statusMsg.setAttribute('aria-live', 'polite');
  // Show mode never builds "Get my code back" (the student's code is never replaced).
  var restoreBtn = showAtLoad ? null : button('challenge-solution-restore', 'runner-btn', SOLUTION_COPY.restore);
  statusBox.appendChild(statusMsg);
  if (restoreBtn) { statusBox.appendChild(restoreBtn); }

  function offerLabel() { return state.showMode ? SOLUTION_COPY.showButton : SOLUTION_COPY.button; }

  // --- "Use the solution" beside Check, so it is reachable after a reload or a closed modal.
  var toolbarBtn = (hasModelAnswer && !piecesOn) ? button('challenge-use-solution', 'runner-btn', offerLabel()) : null;

  var checkBtn = opts.checkBtn;
  var bar = checkBtn && checkBtn.parentNode;
  if (bar && typeof bar.insertBefore === 'function') {
    bar.insertBefore(statusBox, checkBtn);
    if (toolbarBtn) { bar.insertBefore(toolbarBtn, checkBtn); }
  }

  // --- The offer inside the Check result card, above its footer.
  var resultOffer = null;
  var resultBtn = null;
  var card = opts.resultCard;
  if (hasModelAnswer && !piecesOn && card && typeof card.appendChild === 'function') {
    resultOffer = el('div', 'cr-solution');
    resultBtn = button('challenge-result-solution', 'runner-btn', offerLabel());
    resultOffer.appendChild(resultBtn);
    var foot = opts.resultFoot;
    if (foot && foot.parentNode === card && typeof card.insertBefore === 'function') {
      card.insertBefore(resultOffer, foot);
    } else {
      card.appendChild(resultOffer);
    }
  }

  // --- The confirm panel: the surface's own modal pattern (`.modal-backdrop` / `.modal`).
  var confirm = null;
  var confirmUse = null;
  var confirmCancel = null;
  var confirmError = null;
  if (hasModelAnswer && !showAtLoad && doc.body) {
    confirm = el('div', 'modal-backdrop challenge-solution-confirm');
    confirm.id = 'challenge-solution-confirm';
    confirm.setAttribute('id', 'challenge-solution-confirm');
    var dialog = el('div', 'modal');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'challenge-solution-title');
    dialog.setAttribute('aria-describedby', 'challenge-solution-body');
    var title = el('h2', 'modal-title', SOLUTION_COPY.confirmTitle);
    title.id = 'challenge-solution-title';
    title.setAttribute('id', 'challenge-solution-title');
    var bodyText = el('p', 'modal-hint', SOLUTION_COPY.confirmBody);
    bodyText.id = 'challenge-solution-body';
    bodyText.setAttribute('id', 'challenge-solution-body');
    confirmError = el('p', 'challenge-solution-error');
    confirmError.setAttribute('role', 'alert');
    confirmError.hidden = true;
    var actions = el('div', 'modal-actions');
    confirmCancel = button('challenge-solution-cancel', 'runner-btn', SOLUTION_COPY.cancel);
    confirmUse = button('challenge-solution-use', 'runner-btn cr-primary', SOLUTION_COPY.button);
    actions.appendChild(confirmCancel);
    actions.appendChild(confirmUse);
    dialog.appendChild(title);
    dialog.appendChild(bodyText);
    dialog.appendChild(confirmError);
    dialog.appendChild(actions);
    confirm.appendChild(dialog);
    confirm.hidden = true;
    doc.body.appendChild(confirm);
  }

  // --- Show mode: the read-only solution dialog. Built HIDDEN at wire time on a show-mode page
  // (like the confirm), lazily when a reply switches a replace page. Open/close toggles the
  // `hidden` attribute ONLY (isSaveInert's live `.modal-backdrop:not([hidden])` query).
  var view = null;
  var viewTabs = null;
  var viewPanel = null;
  var viewClose = null;
  var viewFiles = [];
  var viewTabEls = [];
  var viewOpen = false;

  function clearNode(node) {
    if (typeof node.replaceChildren === 'function') { node.replaceChildren(); } else { node.textContent = ''; }
  }

  function buildView() {
    if (view || !doc.body) { return; }
    view = el('div', 'modal-backdrop challenge-solution-view');
    view.id = 'challenge-solution-view';
    view.setAttribute('id', 'challenge-solution-view');
    var dlg = el('div', 'modal challenge-solution-dialog');
    dlg.setAttribute('role', 'dialog');
    dlg.setAttribute('aria-modal', 'true');
    dlg.setAttribute('aria-labelledby', 'challenge-solution-view-title');
    dlg.setAttribute('aria-describedby', 'challenge-solution-view-guide');
    var title = el('h2', 'modal-title', SOLUTION_COPY.viewTitle);
    title.setAttribute('id', 'challenge-solution-view-title');
    var guide = el('p', 'modal-hint', SOLUTION_COPY.viewGuide);
    guide.setAttribute('id', 'challenge-solution-view-guide');
    viewTabs = el('div', 'csv-tabs');
    viewTabs.setAttribute('id', 'challenge-solution-view-tabs');
    viewTabs.setAttribute('role', 'tablist');
    viewTabs.setAttribute('aria-label', 'Solution files');
    viewPanel = el('div', 'csv-panel');
    viewPanel.setAttribute('id', 'challenge-solution-view-panel');
    viewPanel.setAttribute('tabindex', '0');
    var actions = el('div', 'modal-actions');
    viewClose = button('challenge-solution-view-close', 'runner-btn cr-primary', SOLUTION_COPY.viewClose);
    actions.appendChild(viewClose);
    dlg.appendChild(title);
    dlg.appendChild(guide);
    dlg.appendChild(viewTabs);
    dlg.appendChild(viewPanel);
    dlg.appendChild(actions);
    view.appendChild(dlg);
    view.hidden = true;
    viewClose.addEventListener('click', function () { closeView(); });
    view.addEventListener('click', function (e) { if (e && e.target === view) { closeView(); } });
    viewTabs.addEventListener('keydown', function (e) {
      var at = viewTabEls.indexOf(e && e.target);
      if (at < 0) { return; }
      var to = -1;
      if (e.key === 'ArrowRight') { to = (at + 1) % viewTabEls.length; }
      else if (e.key === 'ArrowLeft') { to = (at - 1 + viewTabEls.length) % viewTabEls.length; }
      else if (e.key === 'Home') { to = 0; }
      else if (e.key === 'End') { to = viewTabEls.length - 1; }
      if (to < 0) { return; }
      if (typeof e.preventDefault === 'function') { e.preventDefault(); }
      selectTab(to);
      viewTabEls[to].focus();
    });
    doc.body.appendChild(view);
  }

  function renderPanel(file, tabEl) {
    clearNode(viewPanel);
    if (tabEl) {
      viewPanel.setAttribute('role', 'tabpanel');
      viewPanel.setAttribute('aria-labelledby', tabEl.id);
    } else {
      viewPanel.setAttribute('role', 'region');
      viewPanel.setAttribute('aria-label', file.name);
    }
    if (!file.content) {
      viewPanel.appendChild(el('p', 'csv-empty', SOLUTION_COPY.emptyFile));
      return;
    }
    // Plain text only (textContent); the line numbers are a CSS counter, never copied.
    var pre = el('pre', 'csv-code');
    var code = el('code');
    var lines = String(file.content).replace(/\r\n?/g, '\n').split('\n');
    if (lines.length > 1 && lines[lines.length - 1] === '') { lines.pop(); }
    lines.forEach(function (line) { code.appendChild(el('span', 'csv-line', line)); });
    pre.appendChild(code);
    viewPanel.appendChild(pre);
  }

  function selectTab(i) {
    var f = viewFiles[i];
    if (!f) { return; }
    state.viewTab = f.name;
    viewTabEls.forEach(function (t, j) {
      t.setAttribute('aria-selected', j === i ? 'true' : 'false');
      t.setAttribute('tabindex', j === i ? '0' : '-1');
    });
    renderPanel(f, viewTabEls.length ? viewTabEls[i] : null);
  }

  function editorFiles() {
    try { return core.getFiles ? core.getFiles() : null; } catch (e) { return null; }
  }

  function renderTabs() {
    viewFiles = orderSolutionFiles(state.cache, editorFiles());
    viewTabEls = [];
    clearNode(viewTabs);
    var multi = viewFiles.length > 1;
    viewTabs.hidden = !multi;
    if (multi) {
      viewFiles.forEach(function (f, i) {
        var t = button('challenge-solution-tab-' + i, 'csv-tab', f.name);
        t.setAttribute('role', 'tab');
        t.setAttribute('aria-controls', 'challenge-solution-view-panel');
        t.addEventListener('click', function () { selectTab(i); });
        viewTabEls.push(t);
        viewTabs.appendChild(t);
      });
    }
    selectTab(defaultSolutionTab(viewFiles, state.viewTab));
  }

  function viewFocusables() {
    var list = [];
    if (viewTabEls.length) {
      var sel = viewTabEls.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0];
      if (sel) { list.push(sel); }
    }
    list.push(viewPanel, viewClose);
    return list;
  }

  // ONE document-level (bubble) keydown listener while open: Escape closes, Tab is trapped.
  function onViewKey(e) {
    if (!viewOpen || !e) { return; }
    if (e.key === 'Escape') {
      if (typeof e.stopPropagation === 'function') { e.stopPropagation(); }
      if (typeof e.preventDefault === 'function') { e.preventDefault(); }
      closeView();
      return;
    }
    if (e.key !== 'Tab') { return; }
    var list = viewFocusables();
    var at = list.indexOf(doc.activeElement);
    var next;
    if (e.shiftKey) { next = at <= 0 ? list.length - 1 : at - 1; }
    else { next = (at < 0 || at === list.length - 1) ? 0 : at + 1; }
    if (typeof e.preventDefault === 'function') { e.preventDefault(); }
    list[next].focus();
  }

  function openView() {
    buildView();
    if (!view || !state.cache) { return; }
    hideResults();
    renderTabs();
    view.hidden = false;
    if (!viewOpen) { doc.addEventListener('keydown', onViewKey); }
    viewOpen = true;
    viewClose.focus();
  }

  function closeView() {
    if (!view || !viewOpen) { return; }
    view.hidden = true;
    viewOpen = false;
    if (typeof doc.removeEventListener === 'function') { doc.removeEventListener('keydown', onViewKey); }
    var back = (toolbarBtn && !toolbarBtn.hidden && !toolbarBtn.disabled) ? toolbarBtn : opts.checkBtn;
    // solution-pieces-merge: back to the step panel's "Show me the whole solution" link.
    if (state.viewOpener && typeof state.viewOpener.focus === 'function') { back = state.viewOpener; }
    state.viewOpener = null;
    if (back && typeof back.focus === 'function') { back.focus(); }
  }

  function canRestore() { return state.routeRestore || !!state.memoryFiles; }

  function render() {
    var blocked = state.checking || state.busy || state.restoring || scheduleLocked;
    [toolbarBtn, resultBtn].forEach(function (b) {
      if (!b) { return; }
      b.textContent = (state.showMode && state.busy && state.pressed === b)
        ? SOLUTION_COPY.showLoading : offerLabel();
    });
    if (toolbarBtn) {
      toolbarBtn.hidden = !state.offered;
      toolbarBtn.disabled = blocked;
    }
    if (resultOffer) { resultOffer.hidden = !(state.offered && state.resultShown); }
    if (resultBtn) { resultBtn.disabled = blocked; }
    if (confirmUse) { confirmUse.disabled = state.busy || scheduleLocked; }
    if (confirmCancel) { confirmCancel.disabled = state.busy; }
    if (restoreBtn) {
      restoreBtn.hidden = state.showMode || !canRestore();
      restoreBtn.disabled = blocked;
    }
    statusMsg.textContent = state.message;
    if (statusMsg.classList) { statusMsg.classList.toggle('runner-error', state.messageError); }
    statusBox.hidden = (!restoreBtn || restoreBtn.hidden) && !state.message;
  }

  function setMessage(text, isError) {
    state.message = text || '';
    state.messageError = !!isError;
  }

  function focusEditor() {
    try {
      var ed = core.getEditor && core.getEditor();
      if (ed && typeof ed.focus === 'function') { ed.focus(); }
    } catch (e) { /* focus is a courtesy */ }
  }

  function openConfirm(from) {
    if (!confirm || !state.offered || scheduleLocked || state.checking || state.busy || state.restoring) { return; }
    state.opener = from || null;
    if (from === resultBtn) { hideResults(); }
    confirmError.hidden = true;
    confirmError.textContent = '';
    confirmUse.textContent = SOLUTION_COPY.button;
    confirm.hidden = false;
    render();
    if (typeof confirmUse.focus === 'function') { confirmUse.focus(); }
  }

  function closeConfirm(refocus) {
    if (!confirm) { return; }
    confirm.hidden = true;
    if (!refocus) { return; }
    var back = (state.opener && state.opener !== resultBtn && !state.opener.hidden) ? state.opener : toolbarBtn;
    if (back && !back.hidden && typeof back.focus === 'function') { back.focus(); }
  }

  // Save the Draft right after a programmatic load. `success: true` is required (the draft
  // endpoint swallows errors into a 200); anything else keeps the loaded code on screen and says so.
  function saveAfterLoad() {
    return Promise.resolve().then(function () { return flush(); }).then(function (res) {
      if (!(res && res.success === true)) { throw new Error('draft not saved'); }
    }).catch(function () {
      setMessage(SOLUTION_COPY.saveFailed, true);
      render();
    });
  }

  function useSolution() {
    if (state.busy || state.restoring || state.checking || scheduleLocked) { return Promise.resolve(); }
    state.busy = true;
    confirmUse.textContent = SOLUTION_COPY.loading;
    confirmError.hidden = true;
    render();
    var before = null;
    // Cancel the pending autosave and save the student's own code now, so nothing in flight can
    // land on top of the solution afterwards. A failed save does not stop the fetch: the server
    // keeps the posted files as the backup.
    return Promise.resolve().then(function () { return flush(); }).catch(function () { return null; })
      .then(function () {
        before = cleanFiles(core.getFiles());
        return fetchSolution(fetchFn, courseCtx, attemptId, before);
      })
      .then(function (res) {
        // solution-show-mode race: the author switched this challenge to show mode after the page
        // loaded. Never replace the code (there is no backup): close the confirm, cache the files,
        // switch the page into show mode and open the read-only dialog instead.
        if (state.showMode || res.mode === 'show') {
          state.busy = false;
          confirmUse.textContent = SOLUTION_COPY.button;
          closeConfirm(false);
          state.showMode = true;
          state.cache = res.files;
          setMessage('', false);
          render();
          openView();
          return;
        }
        if (!state.memoryFiles && before) { state.memoryFiles = before; }
        state.routeRestore = state.routeRestore || res.hasBackup;
        core.load(solutionLoadPayload(opts.payload, res.files));
        state.busy = false;
        confirmUse.textContent = SOLUTION_COPY.button;
        setMessage(SOLUTION_COPY.loaded, false);
        closeConfirm(false);
        render();
        focusEditor();
        return saveAfterLoad();
      }, function (err) {
        // The editor is untouched: nothing was loaded.
        state.busy = false;
        confirmUse.textContent = SOLUTION_COPY.button;
        confirmError.textContent = (err && err.serverMessage) || SOLUTION_COPY.loadFailed;
        confirmError.hidden = false;
        render();
      });
  }

  // Show mode: the first press POSTs `{ parent, files: null }` (no flush, nothing posted, the
  // editor never touched); later presses reopen from the cache. A page in show mode stays in show
  // mode whatever the reply says.
  function showSolution(from) {
    if (!state.offered || scheduleLocked || state.checking || state.busy || state.restoring) {
      return Promise.resolve();
    }
    if (from && from === resultBtn) { hideResults(); }
    if (state.cache) { openView(); return Promise.resolve(); }
    state.busy = true;
    state.pressed = from || toolbarBtn;
    render();
    return fetchSolution(fetchFn, courseCtx, attemptId, null).then(function (res) {
      state.busy = false;
      state.pressed = null;
      state.cache = res.files;
      setMessage('', false);
      render();
      openView();
    }, function (err) {
      state.busy = false;
      state.pressed = null;
      setMessage((err && err.serverMessage) || SOLUTION_COPY.loadFailed, true);
      render();
    });
  }

  function onOffer(from) {
    if (state.showMode) { showSolution(from); } else { openConfirm(from); }
  }

  function restore() {
    if (state.showMode || !restoreBtn || !canRestore() ||
        state.busy || state.restoring || state.checking || scheduleLocked) {
      return Promise.resolve();
    }
    state.restoring = true;
    restoreBtn.textContent = SOLUTION_COPY.restoring;
    render();
    var viaRoute = state.routeRestore;
    var source = viaRoute
      ? fetchRestore(fetchFn, courseCtx, attemptId).then(function (r) { return r.files; })
      : Promise.resolve(state.memoryFiles);
    return source.then(function (files) {
      core.load(solutionLoadPayload(opts.payload, files));
      // The server marks the backup restored, so the button goes until the next fetch.
      state.routeRestore = false;
      state.memoryFiles = null;
      state.restoring = false;
      restoreBtn.textContent = SOLUTION_COPY.restore;
      setMessage(SOLUTION_COPY.restored, false);
      render();
      focusEditor();
      return saveAfterLoad();
    }, function (err) {
      state.restoring = false;
      restoreBtn.textContent = SOLUTION_COPY.restore;
      setMessage((err && err.serverMessage) || SOLUTION_COPY.restoreFailed, true);
      render();
    });
  }

  if (toolbarBtn) { toolbarBtn.addEventListener('click', function () { onOffer(toolbarBtn); }); }
  if (resultBtn) { resultBtn.addEventListener('click', function () { onOffer(resultBtn); }); }
  if (confirm) {
    confirmUse.addEventListener('click', function () { useSolution(); });
    confirmCancel.addEventListener('click', function () { if (!state.busy) { closeConfirm(true); } });
    confirm.addEventListener('click', function (e) {
      if (e && e.target === confirm && !state.busy) { closeConfirm(true); }
    });
    doc.addEventListener('keydown', function (e) {
      if (e && e.key === 'Escape' && !confirm.hidden && !state.busy) { closeConfirm(true); }
    });
  }
  if (restoreBtn) { restoreBtn.addEventListener('click', function () { restore(); }); }
  if (showAtLoad) { buildView(); }

  render();

  return {
    active: true,
    // One finished Check: a fail (graded or a run failure) counts; a pass does not reset.
    noteCheck: function (passed) {
      var was = state.offered;
      if (!passed) { state.count = bumpFailCount(storage, attemptId); }
      if (hasModelAnswer && state.count >= threshold) { state.offered = true; }
      state.resultShown = true;
      render();
      if (!was && state.offered) {
        state.offeredListeners.forEach(function (cb) {
          try { cb(); } catch (e) { /* a listener never breaks the Check */ }
        });
      }
    },
    setChecking: function (on) {
      state.checking = !!on;
      if (on) { state.resultShown = false; }
      render();
    },
    // solution-pieces-merge: what the step panel (challenge-pieces.js) reads.
    isOffered: function () { return state.offered; },
    onOffered: function (cb) { if (typeof cb === 'function') { state.offeredListeners.push(cb); } },
    checking: function () { return state.checking; },
    locked: function () { return scheduleLocked; },
    // The read-only whole-solution view (5c show mode): fetched on first use, then cached.
    openView: function (opener) {
      state.viewOpener = opener || null;
      return showSolution(null);
    },
    // Test seams.
    _state: state,
    _useSolution: useSolution,
    _showSolution: showSolution,
    _restore: restore
  };
}
